import { EventEmitter } from "@westpierstudio/luma/events";
import { PointLike3D } from "@westpierstudio/luma/factory/PixiFactory";
import { IServiceProvider } from "@westpierstudio/luma/services";
import { Container, DEG_TO_RAD, RAD_TO_DEG, Sprite } from "pixi.js";
import { Animation, Camera, Container3D, Model } from "pixi3d";
import { Cubemap, CubemapFaces, StandardMaterial, Color, LightType, Light } from "pixi3d";

export type Game3DModelAnimation = Animation & EventEmitter;

export type CubeMapSpec = {
    textureFormat: `${string}_{face}_{mipLevel}` // with {face} and {mipLevel} placeholders,
    bestMipMapLevel: number
    worstMipMapLevel: number
}

export function createCubeMap(services: IServiceProvider, cubeMapSpec: CubeMapSpec) {

    const {bestMipMapLevel, worstMipMapLevel} = cubeMapSpec;
    const faces =
        new Array(Math.max(worstMipMapLevel - bestMipMapLevel, 1)).fill(undefined) // create array sized with required number of mipMaps
        .map((_, i) => worstMipMapLevel - i) // fill the aray with the mipMap level numbers
        .map(level => cubeMapSpec.textureFormat.replace(`{mipLevel}`, level.toString())) // map mipMap levels to an array of the texture format with the level appended
        .map(mipmap => Cubemap.faces.map(face => mipmap.replace(`{face}`, face).split(`.`)[0])) // each level is mapped to an array of textures for each face of the cube
        .map(face => ( // each face for each level is then mapped to an array of cubeMapFaces, one for each mipmap level
            <CubemapFaces>{
                posx: services.resources.getTexture(face[0]),
                negx: services.resources.getTexture(face[1]),
                posy: services.resources.getTexture(face[2]),
                negy: services.resources.getTexture(face[3]),
                posz: services.resources.getTexture(face[4]),
                negz: services.resources.getTexture(face[5])
            }
        ));

    faces.reverse();
    return Cubemap.fromFaces(faces);
}

export function createCubeMapFromColor(r?: number, g?: number, b?: number, a?: number) {
    return Cubemap.fromColors(new Color(r, g, b, a));
}

export function toMaterials(model: Model) {
    return model.meshes.map(mesh => mesh.material as StandardMaterial);
}

export function setObjectRotation(object: Container3D, rotation: PointLike3D): void {
    object.rotationQuaternion.setEulerAngles(rotation.x || 0, rotation.y || 0, rotation.z || 0);
}

export function playModelAnim(model: Model, name: string, loop=false): Game3DModelAnimation {

    const anim = model.animations.find(anim => anim.name === name);

    if (!anim) {
        throw new Error(`Anim ${name} not found`);
    }

    anim.loop = loop;
    anim.play();

    return anim as Game3DModelAnimation;
}

export function setModelAnimFrame(model: Model, name: string, frame: number): Game3DModelAnimation {

    const anim = playModelAnim(model, name);

    anim.update(frame);
    anim.stop();

    return anim as Game3DModelAnimation;
}

export function clearAnim(anim?: Game3DModelAnimation): void {

    if (anim) {

        anim.loop = false;
        anim.removeAllListeners();

        //Set to last frame and stop
        anim.update(anim.duration);
        anim.stop();

        anim = undefined;
    }
}

export function setVisibilityForModels(models: Model[], visible: boolean): void {

    for (const item of models) {
        item.visible = visible;
    }
}

export function getModelChild(model: Container3D, name: string): Container3D {

    const children = new Map<string, Container3D[]>();
    findAllModelChildren(model, children);

    const matches = children.get(name);

    if (matches) {
        return matches[0]; //If multiple matches found return 1st
    } else {
        throw new Error(`getModelChild - Child Not found ${name}`);
    }
}

export function printModelChildren(model: Container3D): void {

    const children = new Map<string, Container3D[]>();
    findAllModelChildren(model, children);

    console.log(children);
}

export function fromHorizontalToVerticalFOV(horizontalFOV: number, width: number, height: number): number {
    return 2 * Math.atan(Math.tan((horizontalFOV * DEG_TO_RAD) / 2) * (height / width)) * RAD_TO_DEG;
}

export function fromVerticalToHorizontalFOV(verticalFOV: number, width: number, height: number): number {
    return 2 * Math.atan(Math.tan((verticalFOV * DEG_TO_RAD) / 2) * (width / height)) * RAD_TO_DEG;
}

export function setPosFrom3DPos(target: Container | Sprite, source: Container3D): void {

    const modelPos = source.worldTransform.position;
    const screenPos = Camera.main.worldToScreen(modelPos.x, modelPos.y, modelPos.z);
    const localPos = target.parent.toLocal(screenPos);

    target.position.copyFrom(localPos);
}

function findAllModelChildren(model: Container3D, map: Map<string, Container3D[]>): void {

    for (const child of <Container3D[]>model.children) {

        if (map.has(child.name)) {
            const list = map.get(child.name);
            list?.push(child);
        } else {
            map.set(child.name, [child]);
        }

        findAllModelChildren(child, map);
    }
}

export function createLight(lightType: LightType, options: Partial<Light>) {
    const light = new Light();
    light.type = lightType;
    for (const [key, value] of Object.entries(options)) {
        (<any>light)[key] = value;
    }
    return light;
}

const DefaultFirstPersonControllerOptions = {
    mouseSensitivity: 0.3,
    movementSpeed: 10,
    boostSpeed: 2,
    yaw: 180,
    pitch: 0,
    roll: 0,
    log: false
};

type FirstPersonControllerOptions = typeof DefaultFirstPersonControllerOptions;
export class FirstPersonCameraController {

    lastMouseEvent? :MouseEvent;
    isMouseDown: boolean = false;

    yaw: number;
    pitch: number;
    roll: number;

    forward: number;
    strafe: number;

    boost: boolean;

    mouseSensitivity: number;
    boostSpeed: number;
    movementSpeed: number;

    log: boolean;

    constructor(protected services: IServiceProvider, protected camera: Camera, options?: Partial<FirstPersonControllerOptions>) {

        const fullOptions = {...DefaultFirstPersonControllerOptions, ...options};
        this.log = fullOptions.log;
        this.yaw = fullOptions.yaw;
        this.pitch = fullOptions.pitch;
        this.roll = fullOptions.roll;
        this.mouseSensitivity = fullOptions.mouseSensitivity;
        this.movementSpeed = fullOptions.movementSpeed;
        this.boostSpeed = fullOptions.boostSpeed;

        this.forward = this.strafe = 0;
        this.boost = false;

        document.addEventListener(`mousedown`, () => this.isMouseDown = true);
        document.addEventListener(`mouseup`, () => this.isMouseDown = false);
        document.addEventListener(`mousemove`, this.onMouseMove.bind(this));
        document.addEventListener(`keydown`, this.onKeyDown.bind(this));
        document.addEventListener(`keyup`, this.onKeyUp.bind(this));
        services.application.onTick.add(this.move, this);
        this.updateCamera();
    }

    onMouseMove(e: MouseEvent) {
        if (this.lastMouseEvent) {
            if (this.isMouseDown) {

                const delta = {
                    x: (e.clientX - this.lastMouseEvent.clientX) * this.mouseSensitivity,
                    y: (e.clientY - this.lastMouseEvent.clientY) * this.mouseSensitivity
                };
                this.yaw -= delta.x;
                this.pitch += delta.y;
                this.updateCamera();
            }

        }

        this.lastMouseEvent = e;
    }

    logCamera() {
        if (this.log) {
            const position = this.camera.position;
            console.group(`Camera`);
            console.log(`position`, {x: position.x, y: position.y, z: position.z});
            console.log(`rotation`, {pitch: this.pitch, yaw: this.yaw, roll: this.roll});
            console.groupEnd();
        }
    }

    updateCamera() {
        this.pitch = Math.max(Math.min(this.pitch, 90), -90);
        this.camera.transform.rotationQuaternion.setEulerAngles(this.pitch, this.yaw, 0);
        this.logCamera();
    }

    move(dt: number) {

        if (this.forward || this.strafe) {
            const speed = this.movementSpeed * (this.boost ? this.boostSpeed : 1) * dt;
            const forwardDelta = this.camera.transform.worldTransform.forward.array.map(x => x * this.forward * speed);
            const rightStrafeDelta = this.camera.transform.worldTransform.right.array.map(x => x * this.strafe * speed);

            const position = this.camera.position.array;
            this.camera.position.set(
                position[0] + forwardDelta[0] + rightStrafeDelta[0],
                position[1] + forwardDelta[1] + rightStrafeDelta[1],
                position[2] + forwardDelta[2] + rightStrafeDelta[2]
            );
            this.logCamera();
        }
    }

    onKeyDown(e: KeyboardEvent) {
        const key = e.key.toLocaleLowerCase();
        if (key === `w`) {
            this.forward = 1;
        }
        if (key === `a`) {
            this.strafe = -1;
        }
        if (key === `s`) {
            this.forward = -1;
        }
        if (key === `d`) {
            this.strafe = +1;
        }
        if (key === `shift`) {
            this.boost = true;
        }
    }

    onKeyUp(e: KeyboardEvent) {
        const key = e.key.toLocaleLowerCase();
        if (key === `w`) {
            this.forward = 0;
        }
        if (key === `a`) {
            this.strafe = 0;
        }
        if (key === `s`) {
            this.forward = 0;
        }
        if (key === `d`) {
            this.strafe = 0;
        }
        if (key === `shift`) {
            this.boost = false;
        }
    }
}